import { useState } from "react";

import {
  faCalendar,
  faClock,
  faMapMarkerAlt,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useLocation, useNavigate } from "react-router-dom";

import { useLanguage } from "../../contexts/LanguageContext";
import { useUser } from "../../contexts/UserContext";
import { formatDisplayDate } from "../../utils/dateUtils";
import HeroHeader from "../HeroHeader/HeroHeader";

import "./CreateEvent.css";

const CreateEvent = ({ user }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    eventName: "",
    eventDate: "",
    eventTime: "",
    description: "",
    location: "",
    hostName: user?.name || "",
    hostEmail: user?.email || "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const { isAuthenticated, createEvent } = useUser();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Limpiar mensajes cuando el usuario empiece a escribir
    if (error) setError("");
    if (success) setSuccess("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    // Si el usuario no está autenticado, redirigir a la página de auth
    if (!isAuthenticated) {
      navigate("/auth", {
        state: { from: location },
      });

      return;
    }

    setIsSubmitting(true);

    try {
      const event = await createEvent(formData);
      setSuccess("¡Evento creado exitosamente!");
      navigate("/events/dashboard");
    } catch (error) {
      setError(error?.message || "Error creating event.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="create-event">
      <div>
        <HeroHeader
          title={t("eventCreate.title")}
          subtitle={t("eventCreate.subtitle")}
        />
        <div className="create-event-content">
          <div className="form-section">
            <form onSubmit={handleSubmit} className="event-form">
              {/* Mensajes de error y éxito */}
              {error && (
                <div
                  className="error-message"
                  style={{
                    backgroundColor: "#fee",
                    border: "1px solid #fcc",
                    borderRadius: "4px",
                    padding: "12px",
                    marginBottom: "16px",
                    color: "#c33",
                  }}
                >
                  {error}
                </div>
              )}

              {success && (
                <div
                  className="success-message"
                  style={{
                    backgroundColor: "#efe",
                    border: "1px solid #cfc",
                    borderRadius: "4px",
                    padding: "12px",
                    marginBottom: "16px",
                    color: "#363",
                  }}
                >
                  {success}
                </div>
              )}

              <div className="form-group">
                <label htmlFor="eventName">{t("eventCreate.form.name")}</label>
                <input
                  type="text"
                  id="eventName"
                  name="eventName"
                  value={formData.eventName}
                  onChange={handleInputChange}
                  placeholder={t("eventCreate.form.namePlaceholder")}
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="eventDate">
                    {t("eventCreate.form.date")}
                  </label>
                  <input
                    type="date"
                    id="eventDate"
                    name="eventDate"
                    value={formData.eventDate}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="eventTime">
                    {t("eventCreate.form.time")}
                  </label>
                  <input
                    type="time"
                    id="eventTime"
                    name="eventTime"
                    value={formData.eventTime}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="description">
                  {t("eventCreate.form.description")}
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder={t("eventCreate.form.descriptionPlaceholder")}
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label htmlFor="location">
                  {t("eventCreate.form.location")}
                </label>
                <input
                  type="text"
                  id="location"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  placeholder={t("eventCreate.form.locationPlaceholder")}
                  required
                />
              </div>
              {isAuthenticated ? (
                <button
                  type="submit"
                  className="btn btn-primary btn-large submit-btn"
                  disabled={isSubmitting}
                >
                  {isSubmitting
                    ? t("eventCreate.form.eventName.creating")
                    : t("eventCreate.form.eventName.creatingQr")}
                </button>
              ) : (
                <button
                  onClick={() =>
                    navigate("/auth", {
                      state: { from: location },
                    })
                  }
                  className="btn btn-primary btn-large submit-btn"
                >
                  {t("eventCreate.form.eventName.signInButton")}
                </button>
              )}
            </form>
          </div>

          <div className="preview-section">
            <div className="event-preview">
              <div className="preview-card">
                <div className="preview-header">
                  <h4 className="event-name-preview">
                    {formData.eventName || "Your Event Name"}
                  </h4>
                  <div className="preview-date">
                    {formData.eventDate && formData.eventTime ? (
                      <>
                        <h4 className="preview-date-time">
                          <FontAwesomeIcon icon={faCalendar} />{" "}
                          {formatDisplayDate(formData.eventDate)}
                        </h4>
                        <h4 className="preview-date-time">
                          <FontAwesomeIcon icon={faClock} />{" "}
                          {formData.eventTime}
                        </h4>
                      </>
                    ) : (
                      <h4 className="placeholder-date-preview">
                        <FontAwesomeIcon icon={faCalendar} />{" "}
                        <span>{t("eventCreate.form.eventName.datetime")}</span>
                      </h4>
                    )}
                  </div>
                </div>
                <div className="preview-body">
                  <p>
                    {formData.description ||
                      t("eventCreate.form.descriptionPlaceholder")}
                  </p>
                </div>
                <h4 className="preview-location">
                  <FontAwesomeIcon icon={faMapMarkerAlt} />{" "}
                  {formData.location ||
                    t("eventCreate.form.locationPlaceholder")}
                </h4>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateEvent;
